import {
    NivalizInboundTransactionTimerActivityHourDayHeatmapChart,
    NivalizOutboundTransactionTimerActivityHourDayHeatmapChart,
    hasChartSeries
} from '@frontend/chart/components';
import { CommonMessage } from '@frontend/lang';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { useInsightHeatmapCellClick } from '../heatmap-cell-summary/use-insight-heatmap-cell-click';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { useInsightChartMinHeight } from '../insight-detail-mode.context';
import { InsightId, InsightIdValue } from '../insight-registry/insight-ids';
import { useLast2WeeksNivalizTransactionTimerActivityHourDayHeatmapChart } from './use-last-2-weeks-nivaliz-transaction-timer-activity-hour-day-heatmap-chart';

type HourDayNamedChart = typeof NivalizOutboundTransactionTimerActivityHourDayHeatmapChart | typeof NivalizInboundTransactionTimerActivityHourDayHeatmapChart;

const Last2WeeksNivalizTransactionTimerActivityHourDayHeatmapChartBase = ({
    accountId,
    width,
    insightId,
    Chart,
    title
}: {
    accountId: string;
    width?: InsightCardWidth;
    insightId: InsightIdValue;
    Chart: HourDayNamedChart;
    title: ReactNode;
}) => {
    const chartMinHeight = useInsightChartMinHeight(280);
    const { data, isLoading, rangeStart, dayCount } = useLast2WeeksNivalizTransactionTimerActivityHourDayHeatmapChart(accountId, Chart.definition);
    const onCellClick = useInsightHeatmapCellClick({
        accountId,
        chartKey: Chart.definition.key,
        title
    });

    return (
        <InsightCard
            insightId={insightId}
            chartKey={Chart.definition.key}
            width={width}
            title={title}
            subtitle={
                <FormattedMessage
                    id='platform.analytics.insights.last-2-weeks-nivaliz-transaction-timer-activity-hour-day-heatmap-chart.subtitle-14-days'
                    description='Subtitle on the Nivaliz hourly activity heatmap indicating the chart covers the last 14 days'
                    defaultMessage='last 14 days'
                />
            }
            isLoading={isLoading}>
            {!hasChartSeries(data) || !data ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div
                    className='w-full min-w-0 overflow-hidden'
                    style={{ height: chartMinHeight }}>
                    <Chart
                        data={data}
                        rangeStart={rangeStart}
                        dayCount={dayCount}
                        minHeight={chartMinHeight}
                        onCellClick={onCellClick}
                    />
                </div>
            )}
        </InsightCard>
    );
};

export const Last2WeeksNivalizUrnsTransactionTimerActivityHourDayHeatmapChart = ({ accountId, width }: { accountId: string; width?: InsightCardWidth }) => (
    <Last2WeeksNivalizTransactionTimerActivityHourDayHeatmapChartBase
        accountId={accountId}
        width={width}
        insightId={InsightId.NIVALIZ_URNS_ACTIVITY_HOUR_DAY_HEATMAP}
        Chart={NivalizOutboundTransactionTimerActivityHourDayHeatmapChart}
        title={
            <FormattedMessage
                id='platform.analytics.insights.last-2-weeks-nivaliz-urns-transaction-timer-activity-hour-day-heatmap-chart.title'
                description='Title of the Nivaliz urns insights heatmap showing combined completed drop-off and pick-up timer volume by hour of day over the last 14 days'
                defaultMessage='Urns: Hourly activity heatmap'
            />
        }
    />
);

export const Last2WeeksNivalizCoffinsTransactionTimerActivityHourDayHeatmapChart = ({ accountId, width }: { accountId: string; width?: InsightCardWidth }) => (
    <Last2WeeksNivalizTransactionTimerActivityHourDayHeatmapChartBase
        accountId={accountId}
        width={width}
        insightId={InsightId.NIVALIZ_COFFINS_ACTIVITY_HOUR_DAY_HEATMAP}
        Chart={NivalizInboundTransactionTimerActivityHourDayHeatmapChart}
        title={
            <FormattedMessage
                id='platform.analytics.insights.last-2-weeks-nivaliz-coffins-transaction-timer-activity-hour-day-heatmap-chart.title'
                description='Title of the Nivaliz coffins insights heatmap showing combined completed drop-off and pick-up timer volume by hour of day over the last 14 days'
                defaultMessage='Coffins: Hourly activity heatmap'
            />
        }
    />
);
